// @ts-check
/**
 * CUSTOM: Filtrering + sortering på kollektionssiden – som Abbott Lyon, prisslider inspireret af Missoma (13/10 2026).
 * Markup: snippets/custom-collection-filter.liquid · CSS: assets/custom-collection-filter.css
 *
 * - Hvert klik opdaterer produkterne med det samme (Section Rendering API) + URL'en (replaceState, så "tilbage"
 *   går til forrige side og ikke gennem hvert enkelt filterklik).
 * - Kun den nyeste forespørgsel tæller: en ny afbryder den forrige (AbortController) – ingen "gamle" resultater.
 * - Skuffens DOM erstattes IKKE (så fokus, åben gruppe og et håndtag, man trækker i, bevares) – kun tal,
 *   afkrydsning og deaktivering opdateres. Produkter, chips og antal erstattes.
 */
(function () {
  if (customElements.get('custom-collection-filter')) return;

  /* ---------- Ren logik (ingen DOM) ---------- */

  /**
   * Gør et tastet beløb til et gyldigt helt tal inden for [lo, hi]. Tom/ugyldig tekst → fallback.
   * @param {string} text
   * @param {number} lo
   * @param {number} hi
   * @param {number} fallback
   * @returns {number}
   */
  function parsePrice(text, lo, hi, fallback) {
    const digits = String(text).replace(/[^\d]/g, '');
    if (digits === '') return fallback;
    const n = parseInt(digits, 10);
    if (!Number.isFinite(n)) return fallback;
    return Math.min(hi, Math.max(lo, n));
  }

  /**
   * Sørger for min ≤ max.
   * - Slider ('stop'): håndtaget kan ikke trækkes forbi det andet – det stopper ved det.
   * - Tastet beløb ('swap'): skriver man fx "Til 200" mens "Fra" er 300, menes 200–300 → byt om.
   * @param {number} min
   * @param {number} max
   * @param {'min'|'max'} moved Det håndtag/felt, brugeren ændrede
   * @param {'stop'|'swap'} mode
   * @returns {[number, number]}
   */
  function orderRange(min, max, moved, mode) {
    if (min <= max) return [min, max];
    if (mode === 'swap') return [max, min];
    return moved === 'min' ? [max, max] : [min, min];
  }

  /**
   * Skal prisen med i URL'en? Kun den grænse, der er indsnævret (fuld spænd = intet prisfilter).
   * @param {number} min
   * @param {number} max
   * @param {number} lo
   * @param {number} hi
   * @returns {{ gte: number | null, lte: number | null }}
   */
  function priceParams(min, max, lo, hi) {
    return { gte: min > lo ? min : null, lte: max < hi ? max : null };
  }

  /**
   * Bygger URL-parametrene ud fra formularens felter (deaktiverede felter er udeladt af FormData).
   * Side-nummeret fjernes altid – nye filtre starter på side 1.
   * @param {FormData} data
   * @returns {URLSearchParams}
   */
  function buildParams(data) {
    const params = new URLSearchParams();
    data.forEach((value, key) => {
      if (typeof value === 'string' && value !== '') params.append(key, value);
    });
    params.delete('page');
    return params;
  }

  /* ---------- Komponent ---------- */

  class CustomCollectionFilter extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId || '';
      /** @type {HTMLFormElement | null} */
      this.form = this.querySelector('[data-cf-form]');
      /** @type {HTMLElement | null} */
      this.drawer = this.querySelector('[data-cf-drawer]');
      /** @type {AbortController | null} */
      this.controller = null;
      /** @type {HTMLElement | null} */
      this.lastTrigger = null;
      /** @type {number | undefined} */
      this.priceTimer = undefined;

      /** Filtrene, siden viser lige nu – samme filtre igen giver ingen ny hentning */
      this.currentQuery = this.form ? buildParams(new FormData(this.form)).toString() : '';

      this.bindDrawer();
      this.bindGroups();
      this.bindOptions();
      this.bindPrice();
      this.bindSort();
      this.bindChips();
    }

    /* ----- Skuffe ----- */

    bindDrawer() {
      this.querySelector('[data-cf-open]')?.addEventListener('click', (e) => {
        this.lastTrigger = /** @type {HTMLElement} */ (e.currentTarget);
        this.open();
      });
      this.querySelectorAll('[data-cf-close]').forEach((el) => el.addEventListener('click', () => this.close()));

      // "Vis produkter": produkterne er allerede opdateret – luk bare (uden JS sendes formularen)
      this.form?.addEventListener('submit', (e) => {
        e.preventDefault();
        this.close();
      });

      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) {
          e.stopPropagation();
          this.close();
        }
        if (e.key === 'Tab' && this.classList.contains('is-open')) this.trapFocus(e);
      });
    }

    open() {
      if (!this.drawer) return;
      // Én gruppe åben, når skuffen åbner: den første (som Abbott Lyon), hvis ingen er åben
      const toggles = this.querySelectorAll('[data-cf-group-toggle]');
      if (toggles.length && !this.querySelector('[data-cf-group-toggle][aria-expanded="true"]')) {
        toggles[0].setAttribute('aria-expanded', 'true');
      }
      this.drawer.inert = false;
      this.classList.add('is-open');
      document.documentElement.classList.add('custom-cf-lock');
      this.querySelector('[data-cf-open]')?.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => /** @type {HTMLElement | null} */ (this.querySelector('.custom-cf__close'))?.focus());
    }

    close() {
      if (!this.drawer || !this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.drawer.inert = true;
      document.documentElement.classList.remove('custom-cf-lock');
      this.querySelector('[data-cf-open]')?.setAttribute('aria-expanded', 'false');
      (this.lastTrigger || /** @type {HTMLElement | null} */ (this.querySelector('[data-cf-open]')))?.focus({ preventScroll: true });

      // Er man scrollet forbi værktøjslinjen (fx efter færre resultater), så vis toppen af produkterne
      const toolbar = this.querySelector('.custom-cf__toolbar');
      if (toolbar && toolbar.getBoundingClientRect().top < 0) {
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--custom-header-height')) || 0;
        window.scrollTo({ top: window.scrollY + toolbar.getBoundingClientRect().top - header - 16, behavior: 'smooth' });
      }
    }

    /** @param {KeyboardEvent} e */
    trapFocus(e) {
      if (!this.drawer) return;
      const focusables = /** @type {HTMLElement[]} */ (
        Array.from(this.drawer.querySelectorAll('button, a[href], input:not([type="hidden"])')).filter(
          (el) => !(/** @type {HTMLInputElement} */ (el).disabled) && el.offsetParent !== null
        )
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    /* ----- Grupper: én åben ad gangen ----- */

    bindGroups() {
      this.querySelectorAll('[data-cf-group-toggle]').forEach((toggle) => {
        toggle.addEventListener('click', () => {
          const willOpen = toggle.getAttribute('aria-expanded') !== 'true';
          this.querySelectorAll('[data-cf-group-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
          toggle.setAttribute('aria-expanded', String(willOpen));
        });
      });
    }

    /* ----- Afkrydsning ----- */

    bindOptions() {
      this.form?.addEventListener('change', (e) => {
        const target = /** @type {HTMLElement} */ (e.target);
        if (target.matches('[data-cf-option]')) this.update();
      });
    }

    /* ----- Prisslider ----- */

    bindPrice() {
      const price = /** @type {HTMLElement | null} */ (this.querySelector('[data-cf-price]'));
      if (!price) return;
      this.price = price;
      this.lo = Number(price.dataset.rangeMin) || 0;
      this.hi = Number(price.dataset.rangeMax) || 0;
      /** @type {HTMLInputElement} */
      this.rangeMin = /** @type {HTMLInputElement} */ (price.querySelector('[data-cf-range="min"]'));
      /** @type {HTMLInputElement} */
      this.rangeMax = /** @type {HTMLInputElement} */ (price.querySelector('[data-cf-range="max"]'));
      /** @type {HTMLInputElement} */
      this.numMin = /** @type {HTMLInputElement} */ (price.querySelector('[data-cf-number="min"]'));
      /** @type {HTMLInputElement} */
      this.numMax = /** @type {HTMLInputElement} */ (price.querySelector('[data-cf-number="max"]'));
      this.dragging = false;

      /** Står håndtagene oven på hinanden, når man griber fat, er det retningen der afgør, hvilket der flyttes */
      /** @type {number | null} */
      this.overlapAt = null;
      /** @type {'min'|'max'|null} */
      this.redirect = null;

      /** @param {'min'|'max'} which */
      const onSlide = (which) => {
        const input = which === 'min' ? this.rangeMin : this.rangeMax;
        const v = Number(input.value);
        if (this.overlapAt !== null && this.redirect === null && v !== this.overlapAt) {
          this.redirect = which === 'max' && v < this.overlapAt ? 'min' : which === 'min' && v > this.overlapAt ? 'max' : which;
        }
        if (this.overlapAt !== null && this.redirect && this.redirect !== which) {
          // Det greb-ne håndtag bliver stående – det andet følger fingeren/musen
          const at = this.overlapAt;
          if (this.redirect === 'min') this.setPrice(Math.min(v, at), at, 'min');
          else this.setPrice(at, Math.max(v, at), 'max');
          return;
        }
        const [min, max] = orderRange(Number(this.rangeMin.value), Number(this.rangeMax.value), which, 'stop');
        this.setPrice(min, max, which);
      };

      [['min', this.rangeMin], ['max', this.rangeMax]].forEach(([which, input]) => {
        const el = /** @type {HTMLInputElement} */ (input);
        const w = /** @type {'min'|'max'} */ (which);
        el.addEventListener('pointerdown', () => {
          this.dragging = true;
          this.raise(w);
          this.overlapAt = this.rangeMin.value === this.rangeMax.value ? Number(this.rangeMin.value) : null;
          this.redirect = null;
        });
        el.addEventListener('focus', () => this.raise(w));
        el.addEventListener('input', () => onSlide(w));
        // change: når håndtaget slippes, eller ved hvert tastetryk (piletaster) – samles med en kort pause
        el.addEventListener('change', () => {
          this.dragging = false;
          this.overlapAt = null;
          this.redirect = null;
          this.commitPrice();
        });
      });
      // Slip: gem altid (ved omdirigering ændrer det grebne håndtag ikke værdi, så browseren sender ingen "change").
      // change + pointerup samles af pausen i commitPrice → kun én hentning.
      const release = () => {
        if (!this.dragging) return;
        this.dragging = false;
        this.overlapAt = null;
        this.redirect = null;
        this.commitPrice();
      };
      window.addEventListener('pointerup', release);
      window.addEventListener('pointercancel', release);

      [['min', this.numMin], ['max', this.numMax]].forEach(([which, input]) => {
        const el = /** @type {HTMLInputElement} */ (input);
        const w = /** @type {'min'|'max'} */ (which);
        const apply = () => {
          const fallback = w === 'min' ? this.lo : this.hi;
          const typed = parsePrice(el.value, this.lo, this.hi, fallback);
          const [min, max] = orderRange(
            w === 'min' ? typed : Number(this.rangeMin.value),
            w === 'max' ? typed : Number(this.rangeMax.value),
            w,
            'swap'
          );
          this.setPrice(min, max, w);
          this.commitPrice();
        };
        el.addEventListener('change', apply);
        el.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            apply();
          }
        });
      });

      this.setPrice(Number(this.rangeMin.value), Number(this.rangeMax.value), 'max');
    }

    /** @param {'min'|'max'} which */
    raise(which) {
      this.rangeMin?.toggleAttribute('data-cf-top', which === 'min');
      this.rangeMax?.toggleAttribute('data-cf-top', which === 'max');
    }

    /**
     * Sætter slider, felter, farvet spænd og de skjulte URL-felter – uden at hente.
     * @param {number} min
     * @param {number} max
     * @param {'min'|'max'} [moved]
     */
    setPrice(min, max, moved) {
      if (!this.price || !this.rangeMin || !this.rangeMax) return;
      const lo = this.lo || 0;
      const hi = this.hi || 0;
      this.rangeMin.value = String(min);
      this.rangeMax.value = String(max);
      // Begge håndtag i højre ende: "min" skal ligge øverst, ellers kan de ikke skilles ad
      if (max >= hi && min >= hi) this.raise('min');
      else if (moved) this.raise(moved);
      if (document.activeElement !== this.numMin) this.numMin.value = String(min);
      if (document.activeElement !== this.numMax) this.numMax.value = String(max);
      const span = hi - lo || 1;
      this.price.style.setProperty('--cf-from', ((min - lo) / span) * 100 + '%');
      this.price.style.setProperty('--cf-to', ((max - lo) / span) * 100 + '%');

      const params = priceParams(min, max, lo, hi);
      const gte = /** @type {HTMLInputElement | null} */ (this.price.querySelector('[data-cf-price-param="min"]'));
      const lte = /** @type {HTMLInputElement | null} */ (this.price.querySelector('[data-cf-price-param="max"]'));
      if (gte) {
        gte.disabled = params.gte === null;
        gte.value = params.gte === null ? '' : String(params.gte);
      }
      if (lte) {
        lte.disabled = params.lte === null;
        lte.value = params.lte === null ? '' : String(params.lte);
      }
    }

    commitPrice() {
      // Feltet skal vise den rettede værdi, når man forlader det
      if (this.numMin && this.rangeMin) this.numMin.value = this.rangeMin.value;
      if (this.numMax && this.rangeMax) this.numMax.value = this.rangeMax.value;
      window.clearTimeout(this.priceTimer);
      this.priceTimer = window.setTimeout(() => this.update(), 250);
    }

    resetPrice() {
      if (this.price) this.setPrice(this.lo || 0, this.hi || 0);
    }

    /* ----- Sortering ----- */

    bindSort() {
      const toggle = /** @type {HTMLElement | null} */ (this.querySelector('[data-cf-sort-toggle]'));
      const list = /** @type {HTMLElement | null} */ (this.querySelector('[data-cf-sort-list]'));
      if (!toggle || !list) return;
      const links = () => /** @type {HTMLAnchorElement[]} */ (Array.from(list.querySelectorAll('a')));

      const setOpen = (/** @type {boolean} */ open) => {
        list.hidden = !open;
        toggle.setAttribute('aria-expanded', String(open));
      };

      toggle.addEventListener('click', () => {
        const open = list.hidden;
        setOpen(open);
        if (open) (links().find((a) => a.hasAttribute('aria-current')) || links()[0])?.focus();
      });

      list.addEventListener('keydown', (e) => {
        const items = links();
        const i = items.indexOf(/** @type {HTMLAnchorElement} */ (document.activeElement));
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          items[Math.min(items.length - 1, i + 1)]?.focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          items[Math.max(0, i - 1)]?.focus();
        } else if (e.key === 'Escape') {
          e.stopPropagation();
          setOpen(false);
          toggle.focus();
        }
      });

      document.addEventListener('click', (e) => {
        if (!list.hidden && !(/** @type {Node} */ (e.target) instanceof Node && toggle.parentElement?.contains(/** @type {Node} */ (e.target)))) {
          setOpen(false);
        }
      });

      list.addEventListener('click', (e) => {
        const link = /** @type {HTMLElement} */ (e.target).closest('a[data-cf-sort-value]');
        if (!link) return;
        e.preventDefault();
        const value = link.getAttribute('data-cf-sort-value') || '';
        const param = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-cf-sort-param]'));
        if (param) {
          param.value = value;
          param.disabled = value === this.dataset.defaultSort;
        }
        links().forEach((a) => a.toggleAttribute('aria-current', a === link));
        const current = this.querySelector('[data-cf-sort-current]');
        if (current) current.textContent = link.textContent;
        setOpen(false);
        toggle.focus();
        this.update();
      });
    }

    /* ----- Chips + Ryd filtre ----- */

    bindChips() {
      this.addEventListener('click', (e) => {
        const target = /** @type {HTMLElement} */ (e.target);
        const chip = target.closest('a.custom-cf__chip');
        if (chip) {
          e.preventDefault();
          if (chip.hasAttribute('data-cf-remove-price')) {
            this.resetPrice();
          } else {
            const name = chip.getAttribute('data-cf-remove');
            const value = chip.getAttribute('data-cf-value');
            this.form?.querySelectorAll('[data-cf-option]').forEach((el) => {
              const input = /** @type {HTMLInputElement} */ (el);
              if (input.name === name && input.value === value) input.checked = false;
            });
          }
          // Fokus må ikke forsvinde med den fjernede chip
          if (this.classList.contains('is-open')) /** @type {HTMLElement | null} */ (this.querySelector('.custom-cf__close'))?.focus();
          else /** @type {HTMLElement | null} */ (this.querySelector('[data-cf-open]'))?.focus({ preventScroll: true });
          this.update();
          return;
        }
        if (target.closest('[data-cf-clear]')) {
          this.form?.querySelectorAll('[data-cf-option]').forEach((el) => {
            /** @type {HTMLInputElement} */ (el).checked = false;
          });
          this.resetPrice();
          this.update();
        }
      });
    }

    /* ----- Hent + opdatér ----- */

    update() {
      if (!this.form) return;
      const params = buildParams(new FormData(this.form));
      const query = params.toString();
      if (query === this.currentQuery) return;
      this.currentQuery = query;
      const url = window.location.pathname + (query ? '?' + query : '');
      const fetchUrl = url + (query ? '&' : '?') + 'section_id=' + encodeURIComponent(this.sectionId);

      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.setBusy(true);

      fetch(fetchUrl, { signal: controller.signal, credentials: 'same-origin' })
        .then((res) => {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then((html) => {
          if (controller.signal.aborted) return;
          this.render(new DOMParser().parseFromString(html, 'text/html'));
          window.history.replaceState(window.history.state, '', url);
        })
        .catch((err) => {
          if (err && err.name === 'AbortError') return;
          this.currentQuery = null;
          // Netværksfejl: gå til den filtrerede side på almindelig vis, så kunden ikke står med forkerte produkter
          window.location.href = url;
        })
        .finally(() => {
          if (this.controller === controller) {
            this.controller = null;
            this.setBusy(false);
          }
        });
    }

    /** @param {boolean} busy */
    setBusy(busy) {
      const result = document.getElementById('CollectionAjaxResult');
      if (result) result.setAttribute('aria-busy', String(busy));
    }

    /** @param {Document} doc */
    render(doc) {
      const fresh = doc.querySelector('custom-collection-filter');
      const content = document.getElementById('CollectionAjaxContent');
      const freshContent = doc.getElementById('CollectionAjaxContent');
      if (!fresh || !content || !freshContent) {
        window.location.reload();
        return;
      }

      content.innerHTML = freshContent.innerHTML;

      // Chips (værktøjslinje + skuffe), tom-tilstand, knapper
      const freshChips = fresh.querySelectorAll('[data-cf-chips]');
      this.querySelectorAll('[data-cf-chips]').forEach((list, i) => {
        const src = freshChips[i];
        if (!src) return;
        list.innerHTML = src.innerHTML;
        /** @type {HTMLElement} */ (list).hidden = src.hasAttribute('hidden');
      });
      const freshEmpty = fresh.querySelector('[data-cf-empty]');
      const empty = /** @type {HTMLElement | null} */ (this.querySelector('[data-cf-empty]'));
      if (freshEmpty && empty) empty.hidden = freshEmpty.hasAttribute('hidden');
      this.toggleAttribute('data-empty', fresh.hasAttribute('data-empty'));
      const freshClear = fresh.querySelector('.custom-cf__foot [data-cf-clear]');
      const clear = /** @type {HTMLElement | null} */ (this.querySelector('.custom-cf__foot [data-cf-clear]'));
      if (freshClear && clear) clear.hidden = freshClear.hasAttribute('hidden');

      // Valgmuligheder: afkrydsning + deaktivering (DOM'en bevares). Antal vises ikke (ejers ønske 13/10 2026).
      /** @type {Map<string, HTMLInputElement>} */
      const freshOptions = new Map();
      fresh.querySelectorAll('[data-cf-option]').forEach((el) => {
        const input = /** @type {HTMLInputElement} */ (el);
        freshOptions.set(input.name + '\u0000' + input.value, input);
      });
      this.querySelectorAll('[data-cf-option]').forEach((el) => {
        const input = /** @type {HTMLInputElement} */ (el);
        const src = freshOptions.get(input.name + '\u0000' + input.value);
        if (!src) return;
        input.checked = src.checked;
        input.disabled = src.disabled;
      });

      // Prisen: følg serveren, medmindre kunden er i gang med at trække/skrive
      const freshPrice = /** @type {HTMLElement | null} */ (fresh.querySelector('[data-cf-price]'));
      const typing = document.activeElement === this.numMin || document.activeElement === this.numMax;
      if (freshPrice && this.price && !this.dragging && !typing) {
        this.setPrice(Number(freshPrice.dataset.min), Number(freshPrice.dataset.max));
      }

      // Temaets egne ting på produktkortene (quick shop, animationer) + andre custom-scripts
      const t = /** @type {any} */ (window).theme;
      if (t && typeof t.reinitProductGridItem === 'function') t.reinitProductGridItem();
      document.dispatchEvent(new CustomEvent('collection:reloaded'));
    }
  }

  customElements.define('custom-collection-filter', CustomCollectionFilter);

  /** Ren logik eksponeret til test i konsollen */
  /** @type {any} */ (CustomCollectionFilter).logic = { parsePrice, orderRange, priceParams, buildParams };
})();
